import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Megaphone, Sparkles, Trash2, Mail, MessageSquare, Instagram, Facebook, Linkedin } from "lucide-react";
import { toast } from "sonner";

const ICONS = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin };

export default function CampaignManager() {
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("lead_generation");
  const [brief, setBrief] = useState("");
  const [tone, setTone] = useState("Professional");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: ts }, { data: list }] = await Promise.all([
      api.get("/campaign-manager/types"),
      api.get("/campaign-manager"),
    ]);
    setTypes(ts);
    setItems(list);
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    if (!brief.trim()) {
      toast.error("Add a campaign brief");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/campaign-manager/generate", { type, brief, tone });
      setItems([data, ...items]);
      setBrief("");
      toast.success("Campaign bundle generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/campaign-manager/${id}`);
    setItems(items.filter((x) => x.id !== id));
  };

  return (
    <div className="space-y-8" data-testid="campaign-manager-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Campaign Manager</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Full-funnel in one click.</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Pick a campaign type, brief the AI once — get strategy, ad copy, social posts (IG/FB/LinkedIn), email, WhatsApp, and landing page in a single bundle.
        </p>
      </header>

      <div className="glass rounded-2xl p-6 lg:p-8">
        <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Campaign type</div>
        <div className="flex flex-wrap gap-2 mb-5">
          {types.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              data-testid={`campaign-type-${t.id}`}
              className={`text-sm px-4 py-2 rounded-lg ${type === t.id ? "brand-gradient" : "glass hover:bg-white/5"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Brief</label>
            <textarea
              rows={2}
              value={brief}
              data-testid="campaign-brief-input"
              onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. Launch summer linen collection, target metro buyers, push 30% intro discount"
              className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Tone</label>
            <select value={tone} onChange={(e) => setTone(e.target.value)} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm">
              {["Professional", "Bold", "Friendly", "Luxury", "Playful", "Inspirational"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <button
          onClick={generate}
          data-testid="campaign-bundle-generate"
          disabled={busy}
          className="mt-5 inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange disabled:opacity-50"
        >
          <Sparkles className="h-4 w-4" /> {busy ? "Building campaign…" : "Generate full bundle"}
        </button>
      </div>

      <section>
        <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Campaign library</div>
        {items.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">
            <Megaphone className="h-8 w-8 mx-auto text-white/30 mb-3" />
            No campaigns yet — generate your first above.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((c) => <Bundle key={c.id} c={c} onDelete={del} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function Bundle({ c, onDelete }) {
  const b = c.bundle || {};
  return (
    <div className="glass rounded-2xl p-6 lg:p-8" data-testid={`campaign-bundle-${c.id}`}>
      <div className="flex items-start justify-between mb-5 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-mono">{c.type_label}</div>
          <h3 className="font-heading text-2xl font-black mt-1">{b.strategy?.objective}</h3>
          <div className="text-xs text-white/50 mt-1">Brief: {c.brief}</div>
        </div>
        <button onClick={() => onDelete(c.id)} className="p-1.5 rounded-lg hover:bg-white/5">
          <Trash2 className="h-4 w-4 text-white/40" />
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <Card title="Strategy">
          <div className="text-xs text-white/50">Duration · Budget</div>
          <div className="text-sm">{b.strategy?.duration} · {b.strategy?.budget_hint}</div>
          <div className="text-xs text-white/50 mt-3">KPIs</div>
          <ul className="text-xs text-white/80">
            {(b.strategy?.kpis || []).map((k, i) => <li key={i}>· {k}</li>)}
          </ul>
        </Card>
        <Card title="Ad copy">
          <div className="font-bold">{b.ad_copy?.headline}</div>
          <p className="text-xs text-white/70 mt-1">{b.ad_copy?.primary_text}</p>
          <div className="mt-2 brand-gradient inline-block px-3 py-1 rounded text-xs font-semibold">{b.ad_copy?.cta}</div>
        </Card>
        <Card title="Landing page">
          <div className="font-heading font-bold">{b.landing_page?.headline}</div>
          <div className="text-xs text-white/60 mt-1">{b.landing_page?.subheadline}</div>
          <ul className="mt-2 text-xs text-white/70">
            {(b.landing_page?.bullets || []).map((bu, i) => <li key={i}>· {bu}</li>)}
          </ul>
          <div className="mt-2 brand-gradient inline-block px-3 py-1 rounded text-xs font-semibold">{b.landing_page?.cta_button}</div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        {(b.social_posts || []).map((p, i) => {
          const Icon = ICONS[p.platform] || Megaphone;
          return (
            <div key={i} className="border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="h-4 w-4 text-orange-400" />
                <span className="text-xs uppercase tracking-[0.2em] text-white/50">{p.platform}</span>
              </div>
              <p className="text-sm text-white/80 whitespace-pre-wrap">{p.caption}</p>
              <div className="mt-2 text-xs text-orange-400 font-mono">{(p.hashtags || []).map((h) => `#${String(h).replace(/^#/, "")}`).join(" ")}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Email" icon={Mail}>
          <div className="font-bold">{b.email_subject}</div>
          <p className="text-xs text-white/70 whitespace-pre-wrap mt-1">{b.email_body}</p>
        </Card>
        <Card title="WhatsApp" icon={MessageSquare}>
          <p className="text-sm text-white/80 whitespace-pre-wrap">{b.whatsapp_message}</p>
        </Card>
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Creative ideas</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {(b.creative_ideas || []).map((idea, i) => (
            <div key={i} className="border border-white/5 rounded-lg p-3 text-xs text-white/70">{idea}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="border border-white/5 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="h-4 w-4 text-orange-400" />}
        <div className="text-xs uppercase tracking-[0.2em] text-white/50">{title}</div>
      </div>
      {children}
    </div>
  );
}
